import { Alert, Button, Space, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { probeHardwareService } from '../domain/labelPrinting';
import {
    canReadScale,
    describeScale,
    detectScale,
    scaleErrorKey,
    type ScaleResult,
    type ScaleStatus,
} from '../domain/scaleReader';

const { Text } = Typography;

/**
 * This workstation's scale: whether one has been found, and a "Test read" that searches the serial
 * ports again and reads the current weight.
 *
 * There is nothing to set by hand (owner decision): the service finds the scale itself, and this
 * card is where an installer or operator checks that it did, or starts the search over after
 * plugging the scale into another port.
 */
export default function ScaleTestCard() {
    const { t } = useTranslation();
    const [available, setAvailable] = useState<boolean | null>(null);
    const [remembered, setRemembered] = useState<ScaleStatus | null>(null);
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState<ScaleResult | null>(null);

    useEffect(() => {
        let active = true;
        const fetchImpl = window.fetch.bind(window);

        void (async () => {
            const offered = canReadScale(await probeHardwareService(fetchImpl));
            const status = offered ? await describeScale(fetchImpl) : null;

            if (active) {
                setAvailable(offered);
                setRemembered(status);
            }
        })();

        return () => {
            active = false;
        };
    }, []);

    const handleTest = async () => {
        const fetchImpl = window.fetch.bind(window);

        setTesting(true);
        setResult(null);

        try {
            setResult(await detectScale(fetchImpl));
            setRemembered(await describeScale(fetchImpl));
        } finally {
            setTesting(false);
        }
    };

    return (
        <div data-testid="scale-test-panel" style={{ marginTop: 24 }}>
            <Text strong>{t('scale.testCard.title')}</Text>
            <br />
            <Text type="secondary">{t('scale.testCard.description')}</Text>

            {available === false && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                    data-testid="scale-unavailable"
                    message={t('scale.testCard.unavailable')}
                />
            )}

            {available === true && (
                <Space direction="vertical" style={{ marginTop: 16, width: '100%' }}>
                    <Space>
                        <Tag
                            color={remembered?.detected ? 'green' : 'default'}
                            data-testid="scale-status"
                        >
                            {remembered?.detected
                                ? t('scale.testCard.detected', { port: remembered.port })
                                : t('scale.testCard.notDetected')}
                        </Tag>
                        <Button
                            loading={testing}
                            data-testid="scale-test-read"
                            onClick={() => void handleTest()}
                        >
                            {t('scale.testCard.testRead')}
                        </Button>
                    </Space>

                    {result?.ok === true && (
                        <Alert
                            type="success"
                            showIcon
                            data-testid="scale-test-result"
                            message={t('scale.testCard.weight', {
                                grams: result.reading.grams.toFixed(2),
                            })}
                        />
                    )}

                    {result?.ok === false && (
                        <Alert
                            type="error"
                            showIcon
                            data-testid="scale-test-result"
                            message={t(scaleErrorKey(result.code))}
                        />
                    )}
                </Space>
            )}
        </div>
    );
}
